import compact from 'lodash/compact';
import isEmpty from 'lodash/isEmpty';
import isNil from 'lodash/isNil';

import { findStructuredCommunication } from './accounting/bankutils';
import {
  DEFAULT_COMMERCIAL_LEASE_REMITTANCE_TYPE,
  DEFAULT_RESIDENTIAL_LEASE_REMITTANCE_TYPE,
  getSettingValueOrDefault,
} from './settingutils';
import { Invoice, Lease, LeaseRemittanceType, LeaseType, Setting, SettingType } from './types/model-app';

type RemittanceConfiguration = Pick<Lease, 'remittanceInformation' | 'remittanceInformationType'> &
  Partial<Pick<Lease, 'invoiceSenderId'>>;

export interface LeaseRemittanceConfiguration extends RemittanceConfiguration {
  advancedChargesInvoices?: RemittanceConfiguration | null;
}

export const COMMERCIAL_LEASE_TYPES = [LeaseType.COMMERCIAL, LeaseType.OFFICE, LeaseType.WAREHOUSE];

export const isCommercialLeaseType = (leaseType: LeaseType | null | undefined): boolean =>
  !isNil(leaseType) && COMMERCIAL_LEASE_TYPES.includes(leaseType);

export const isLeaseRemittanceType = (value: string): value is LeaseRemittanceType =>
  Object.values(LeaseRemittanceType).includes(value as LeaseRemittanceType);

export const getLeaseRemittanceTypeSettingType = (leaseType: LeaseType | null | undefined): SettingType =>
  isCommercialLeaseType(leaseType)
    ? SettingType.LEASE_REMITTANCE_TYPE_COMMERCIAL
    : SettingType.LEASE_REMITTANCE_TYPE_RESIDENTIAL;

export const getDefaultLeaseRemittanceType = (
  leaseType: LeaseType | null | undefined,
  settings?: Setting[]
): LeaseRemittanceType => {
  const settingType = getLeaseRemittanceTypeSettingType(leaseType);
  const value = getSettingValueOrDefault(settingType, settings);
  if (isLeaseRemittanceType(value)) {
    return value;
  }
  return settingType === SettingType.LEASE_REMITTANCE_TYPE_COMMERCIAL
    ? DEFAULT_COMMERCIAL_LEASE_REMITTANCE_TYPE
    : DEFAULT_RESIDENTIAL_LEASE_REMITTANCE_TYPE;
};

const getRemittanceConfigurations = (lease: LeaseRemittanceConfiguration): RemittanceConfiguration[] =>
  compact([lease, lease.advancedChargesInvoices]);

export const issuesPerInvoiceReferences = (lease: LeaseRemittanceConfiguration): boolean =>
  getRemittanceConfigurations(lease).some(
    (configuration) => configuration.remittanceInformationType === LeaseRemittanceType.STRUCTURED_BY_INVOICE
  );

// A separate charges document is issued by the sender of the charges stream; when the sender cannot tell the
// streams apart, a document only counts as issued with its own reference if every candidate stream issues one
export const isInvoiceIssuedWithItsOwnReference = (
  lease: LeaseRemittanceConfiguration,
  invoice: Partial<Pick<Invoice, 'invoiceSenderId'>>
): boolean => {
  const configurations = getRemittanceConfigurations(lease);
  const senderConfigurations = configurations.filter(
    (configuration) => !isNil(invoice.invoiceSenderId) && configuration.invoiceSenderId === invoice.invoiceSenderId
  );
  return (isEmpty(senderConfigurations) ? configurations : senderConfigurations).every(
    (configuration) => configuration.remittanceInformationType === LeaseRemittanceType.STRUCTURED_BY_INVOICE
  );
};

export const getLeaseFixedReferences = (lease: LeaseRemittanceConfiguration): string[] =>
  compact(
    getRemittanceConfigurations(lease).map((configuration) =>
      findStructuredCommunication(configuration.remittanceInformation)
    )
  );
